import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import api from 'api';
import type { ApplicationRetainersList } from 'types';

export const applicationRetainersKeys = {
    all: ['application-retainers'] as const,
    byApplication: (applicationId: number) =>
        ['application-retainers', 'application', applicationId] as const,
    byApplications: (applicationIds: number[]) =>
        ['application-retainers', 'many', ...applicationIds] as const,
    document: (applicationId: number, agreementId: number) =>
        ['application-retainers', 'document', applicationId, agreementId] as const,
};

/** Retainer row + PDF blob for the client signing page. */
export const useApplicationRetainerDocument = (
    applicationId?: number | null,
    agreementId?: number | null
) => {
    const enabled = Boolean(applicationId) && Boolean(agreementId);

    return useQuery({
        queryKey: applicationRetainersKeys.document(applicationId ?? 0, agreementId ?? 0),
        queryFn: () =>
            api.applicationRetainers.getRetainerWithDocument(
                applicationId as number,
                agreementId as number
            ),
        enabled,
        staleTime: 0,
    });
};

export const useApplicationRetainers = (
    applicationId?: number | null,
    options?: { enabled?: boolean }
) => {
    const enabled = (options?.enabled ?? true) && Boolean(applicationId);

    return useQuery<ApplicationRetainersList>({
        queryKey: applicationRetainersKeys.byApplication(applicationId ?? 0),
        queryFn: async () => {
            const response = await api.applicationRetainers.list(applicationId as number);
            return response.data;
        },
        enabled,
    });
};

export const useApplicationRetainersLists = (
    applicationIds: number[],
    options?: { enabled?: boolean }
) => {
    const sortedIds = [...new Set(applicationIds.filter((id) => Number.isFinite(id)))].sort(
        (a, b) => a - b
    );
    const enabled = (options?.enabled ?? true) && sortedIds.length > 0;

    return useQuery<Record<number, ApplicationRetainersList>>({
        queryKey: applicationRetainersKeys.byApplications(sortedIds),
        queryFn: async () => {
            const responses = await Promise.all(
                sortedIds.map((id) => api.applicationRetainers.list(id))
            );
            return Object.fromEntries(
                responses.map((response, index) => [sortedIds[index], response.data])
            );
        },
        enabled,
    });
};

/** Refetch after create/update/send/sign/delete — `allowed_types` changes with each. */
export const useInvalidateApplicationRetainers = () => {
    const queryClient = useQueryClient();

    return useCallback(
        (applicationId?: number) => {
            if (applicationId) {
                void queryClient.invalidateQueries({
                    queryKey: applicationRetainersKeys.byApplication(applicationId),
                });
            }
            void queryClient.invalidateQueries({ queryKey: applicationRetainersKeys.all });
        },
        [queryClient]
    );
};
